/**
 * Format an ISO 8601 date string (YYYY-MM-DD) as "Mon DD, YYYY" (e.g. "Mar 31, 2027").
 *
 * Parses the components explicitly so a date like "2027-03-31" renders as March 31
 * regardless of the user's timezone. `new Date("2027-03-31")` would parse as UTC
 * midnight and shift a day earlier in timezones west of UTC.
 *
 * Returns an empty string for null/undefined, and the raw input for malformed values.
 */
export function formatDueDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * Format an ISO 8601 timestamp (e.g. "2026-03-15T14:22:00Z") as "Mon DD, YYYY"
 * (e.g. "Mar 15, 2026"). Unlike `formatDueDate`, this accepts a full datetime.
 * Returns an empty string for null/undefined/invalid input.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(ts));
}

/**
 * Format a timestamp as a relative time phrase ("just now", "2 hours ago",
 * "3 days ago", etc.) using `Intl.RelativeTimeFormat`. Returns an empty string
 * for null/undefined/invalid input. The `now` parameter is for testability.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const diffMs = ts - now.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (absSec < 60) return "just now";
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (absSec < 86_400) return rtf.format(Math.round(diffSec / 3600), "hour");
  if (absSec < 2_592_000) return rtf.format(Math.round(diffSec / 86_400), "day");
  if (absSec < 31_536_000) return rtf.format(Math.round(diffSec / 2_592_000), "month");
  return rtf.format(Math.round(diffSec / 31_536_000), "year");
}
